/**
 * iter275 — Meta Lead Ads integration settings page.
 * Admin-only. Handles: Connect Meta OAuth start, Page list + subscription,
 * per-form activation + field mapping, sync-log viewer, Test Connection,
 * Sync Now.
 * Backend: /api/integrations/meta/*  (see backend/routes/meta_leads.py).
 */
import React, { useEffect, useState } from 'react';
import { Loader2, RefreshCw, Play, TestTube, ExternalLink, PlugZap, Unplug } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

// iter278 — fallback catalogue used only if `/integrations/meta/crm-fields`
// is unreachable. Kept intentionally small — the backend endpoint is the
// single source of truth (see `backend/routes/meta_leads.py`).
const FALLBACK_CRM_FIELDS = [
  { value: '', label: '(preserve as raw)', type: 'str' },
  { value: 'hotel_name', label: 'Hotel Name', type: 'str' },
  { value: 'owner_name', label: 'Owner Name', type: 'str' },
  { value: 'phone', label: 'Mobile Number', type: 'str' },
  { value: 'email', label: 'Email Address', type: 'str' },
];

export default function MetaLeadAdsSettings() {
  const [status, setStatus] = useState(null);
  const [pages, setPages] = useState([]);
  const [forms, setForms] = useState([]);
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [crmFields, setCrmFields] = useState(null);

  const loadStatus = async () => { try { setStatus((await api.get('/integrations/meta/status')).data); } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load status'); } };
  const loadPages = async () => { try { setPages((await api.get('/integrations/meta/pages')).data || []); } catch { /* not connected */ } };
  const loadForms = async () => { try { setForms((await api.get('/integrations/meta/forms')).data || []); } catch { /* not connected */ } };
  const loadLogs = async () => { try { setLogs((await api.get('/integrations/meta/logs?limit=50')).data || []); } catch { /* empty */ } };
  const loadCrmFields = async () => {
    try {
      const r = await api.get('/integrations/meta/crm-fields');
      const list = r?.data?.data || [];
      if (Array.isArray(list) && list.length) {
        // Prepend the "preserve as raw" sentinel option.
        setCrmFields([{ value: '', label: '(preserve as raw)', type: 'str' }, ...list]);
        return;
      }
    } catch { /* fall back below */ }
    setCrmFields(FALLBACK_CRM_FIELDS);
  };

  useEffect(() => {
    loadStatus(); loadPages(); loadForms(); loadLogs(); loadCrmFields();
    // If we just returned from the OAuth callback, clear the ?connected= param.
    if (window.location.search.includes('connected=1')) {
      toast.success('Meta connected!');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const connectMeta = async () => {
    try {
      const r = await api.get('/integrations/meta/oauth/start');
      window.location.href = r.data.authorize_url;
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start OAuth'); }
  };
  const disconnect = async () => {
    if (!window.confirm('Disconnect Meta? Pages will be unsubscribed on next connect.')) return;
    await api.post('/integrations/meta/disconnect'); await loadStatus(); await loadPages(); toast.success('Disconnected.');
  };
  const refreshPages = async () => {
    setBusy(true);
    try {
      const r = await api.post('/integrations/meta/pages/refresh');
      toast.success(`Loaded ${r.data.count} page(s).`); await loadPages(); await loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };
  const togglePageSubscription = async (page, on) => {
    setBusy(true);
    try {
      await api.post(`/integrations/meta/pages/${page.page_id}/${on ? 'subscribe' : 'unsubscribe'}`);
      toast.success(on ? 'Page subscribed.' : 'Page unsubscribed.'); await loadPages(); await loadStatus();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };
  const loadFormsForPage = async (page) => {
    setBusy(true);
    try { await api.get(`/integrations/meta/pages/${page.page_id}/forms`); await loadForms(); toast.success('Forms refreshed.'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };
  const toggleForm = async (form, on) => {
    try { await api.put(`/integrations/meta/forms/${form.form_id}`, { active: on }); await loadForms(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const saveMapping = async (form, mapping) => {
    try { await api.put(`/integrations/meta/forms/${form.form_id}`, { field_mapping: mapping }); toast.success('Mapping saved.'); await loadForms(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const testConnection = async () => {
    setBusy(true);
    try { const r = await api.post('/integrations/meta/test'); toast[r.data.ok ? 'success' : 'error'](r.data.ok ? `OK — user ${r.data.user?.name}` : (r.data.detail || 'Failed')); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };
  const syncNow = async () => {
    setBusy(true);
    try { const r = await api.post('/integrations/meta/sync-now', {}); toast.success(`Processed ${r.data.processed} · imported ${r.data.imported} · duplicate ${r.data.duplicate}`); await loadLogs(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };

  const dot = (ok) => <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${ok ? 'bg-emerald-500' : 'bg-red-500'}`} />;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="meta-settings-root">
      <div>
        <h1 className="text-2xl font-semibold">Meta Lead Ads</h1>
        <p className="text-sm text-gray-500">Automatically receive Facebook &amp; Instagram Lead Ads directly into CRM.</p>
      </div>

      {/* Status card */}
      <div className="bg-white border rounded-lg p-5 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-medium text-gray-700 flex items-center">
              {dot(status?.connected)} {status?.connected ? 'Connected' : 'Not Connected'}
            </div>
            {status?.connected_at && <div className="text-xs text-gray-500 mt-0.5">Since {new Date(status.connected_at).toLocaleString()}</div>}
          </div>
          <div className="flex gap-2">
            {!status?.connected ? (
              <button onClick={connectMeta} disabled={!status?.configured} className="px-3 py-2 rounded-lg bg-teal-600 text-white text-sm hover:bg-teal-700 disabled:opacity-40 flex items-center gap-1.5" data-testid="meta-connect-btn">
                <PlugZap size={14} /> Connect Meta
              </button>
            ) : (
              <>
                <button onClick={testConnection} disabled={busy} className="px-3 py-2 rounded-lg border text-sm flex items-center gap-1.5" data-testid="meta-test-btn"><TestTube size={14} /> Test Connection</button>
                <button onClick={syncNow} disabled={busy} className="px-3 py-2 rounded-lg border text-sm flex items-center gap-1.5" data-testid="meta-sync-btn"><Play size={14} /> Sync Now</button>
                <button onClick={disconnect} className="px-3 py-2 rounded-lg border border-red-200 text-red-600 text-sm flex items-center gap-1.5" data-testid="meta-disconnect-btn"><Unplug size={14} /> Disconnect</button>
              </>
            )}
          </div>
        </div>
        {!status?.configured && (
          <div className="text-xs bg-amber-50 text-amber-800 border border-amber-200 rounded p-3">
            Set <code>META_APP_ID</code>, <code>META_APP_SECRET</code>, and <code>META_WEBHOOK_VERIFY_TOKEN</code> in <code>backend/.env</code> then redeploy. Configure webhook URL <b>{status?.webhook_url}</b> in your Meta App dashboard with the same verify token.
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm pt-2">
          <div><div className="text-xs text-gray-500">Pages</div><div>{status?.pages || 0} <span className="text-xs text-gray-400">({status?.pages_subscribed || 0} subscribed)</span></div></div>
          <div><div className="text-xs text-gray-500">Active Forms</div><div>{status?.active_forms || 0}</div></div>
          <div><div className="text-xs text-gray-500">Last Lead</div><div className="text-xs">{status?.last_lead_at ? new Date(status.last_lead_at).toLocaleString() : '—'}</div></div>
          <div><div className="text-xs text-gray-500">Webhook</div><div className="text-xs break-all">{status?.webhook_url}</div></div>
        </div>
      </div>

      {/* Pages */}
      {status?.connected && (
        <div className="bg-white border rounded-lg p-5 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-medium text-gray-800">Connected Pages</h2>
            <button onClick={refreshPages} disabled={busy} className="text-sm flex items-center gap-1.5"><RefreshCw size={14} /> Refresh</button>
          </div>
          {pages.length === 0 ? <div className="text-sm text-gray-500">No pages loaded. Click Refresh.</div> : (
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-gray-500"><th className="py-1.5">Page</th><th>ID</th><th>Status</th><th className="text-right">Actions</th></tr></thead>
              <tbody>
                {pages.map((p) => (
                  <tr key={p.page_id} className="border-t" data-testid={`meta-page-row-${p.page_id}`}>
                    <td className="py-2">{p.name || '(unnamed)'}</td>
                    <td className="text-xs text-gray-500">{p.page_id}</td>
                    <td>{dot(p.leadgen_subscribed)}{p.leadgen_subscribed ? 'Subscribed' : 'Not Subscribed'}</td>
                    <td className="text-right space-x-2">
                      <button onClick={() => togglePageSubscription(p, !p.leadgen_subscribed)} disabled={busy} className="text-xs text-teal-700">{p.leadgen_subscribed ? 'Unsubscribe' : 'Subscribe'}</button>
                      <button onClick={() => loadFormsForPage(p)} disabled={busy} className="text-xs text-gray-700">Load Forms</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Forms + Mapping */}
      {status?.connected && forms.length > 0 && (
        <div className="bg-white border rounded-lg p-5 space-y-4">
          <h2 className="font-medium text-gray-800">Lead Forms &amp; Field Mapping</h2>
          {forms.map((f) => (
            <FormMapping key={f.form_id} form={f} onToggle={toggleForm} onSave={saveMapping} pages={pages} crmFields={crmFields || FALLBACK_CRM_FIELDS} />
          ))}
        </div>
      )}

      {/* Logs */}
      <div className="bg-white border rounded-lg p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-medium text-gray-800">Meta Lead Logs</h2>
          <button onClick={loadLogs} className="text-sm flex items-center gap-1.5"><RefreshCw size={14} /> Refresh</button>
        </div>
        {logs.length === 0 ? <div className="text-sm text-gray-500">No leads received yet.</div> : (
          <table className="w-full text-xs">
            <thead><tr className="text-left text-gray-500"><th className="py-1">Time</th><th>Lead</th><th>Form</th><th>Status</th><th>Detail</th></tr></thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.leadgen_id} className="border-t">
                  <td className="py-1.5">{l.created_at ? new Date(l.created_at).toLocaleString() : '—'}</td>
                  <td>{l.name || <span className="text-gray-400">—</span>}</td>
                  <td className="text-gray-500">{l.form_id}</td>
                  <td><span className={`px-1.5 py-0.5 rounded ${l.status === 'imported' ? 'bg-emerald-50 text-emerald-700' : l.status === 'duplicate' ? 'bg-blue-50 text-blue-700' : l.status === 'failed' ? 'bg-red-50 text-red-700' : 'bg-gray-50 text-gray-700'}`}>{l.status}</span></td>
                  <td className="text-gray-500">{l.error || l.lead_id || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="text-xs text-gray-500 flex items-center gap-1">
        <ExternalLink size={12} /> Docs — App dashboard: developers.facebook.com/apps · Redirect: <code className="ml-1">{status?.redirect_uri}</code>
      </div>
    </div>
  );
}

function FormMapping({ form, onToggle, onSave, pages, crmFields }) {
  const [mapping, setMapping] = useState(form.field_mapping || {});
  const pageName = (pages.find((p) => p.page_id === form.page_id) || {}).name || form.page_id;
  const questions = form.questions || [];
  const set = (k, v) => setMapping({ ...mapping, [k]: v });
  const options = Array.isArray(crmFields) && crmFields.length ? crmFields : FALLBACK_CRM_FIELDS;
  return (
    <div className="border rounded p-3 space-y-2" data-testid={`meta-form-${form.form_id}`}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium">{form.name} <span className="text-xs text-gray-400">· {form.form_id}</span></div>
          <div className="text-xs text-gray-500">Page: {pageName} · Status: {form.status}</div>
        </div>
        <label className="text-xs flex items-center gap-1.5">
          <input type="checkbox" checked={!!form.active} onChange={(e) => onToggle(form, e.target.checked)} data-testid={`meta-form-active-${form.form_id}`} />
          Import into CRM
        </label>
      </div>
      {questions.length > 0 && (
        <>
          <div className="grid grid-cols-2 gap-2 pt-2 border-t">
            {questions.map((q) => {
              const k = (q.key || q.id || '').toLowerCase();
              return (
                <React.Fragment key={k}>
                  <div className="text-xs pt-1.5">{q.label || q.key || q.id}</div>
                  <select value={mapping[k] || ''} onChange={(e) => set(k, e.target.value)} className="text-xs border rounded px-2 py-1" data-testid={`meta-map-${form.form_id}-${k}`}>
                    {options.map((c) => (
                      <option key={c.value || '__raw__'} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </React.Fragment>
              );
            })}
          </div>
          <div className="text-right pt-2">
            <button onClick={() => onSave(form, mapping)} className="text-xs px-3 py-1.5 bg-teal-600 text-white rounded" data-testid={`meta-map-save-${form.form_id}`}>Save Mapping</button>
          </div>
        </>
      )}
    </div>
  );
}
